// frontend/modules/customer-category/components/CustomerCategoryDocumentRequirements.tsx
import React, { useState } from 'react';
import {
    Table,
    Button,
    Space,
    Switch,
    Input,
    InputNumber,
    Select,
    Modal,
    Form,
    message,
    Tag,
    Tooltip,
    Popconfirm
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import {useGetAllDocumentTypes} from "@/shared/api/client/document-type-controller";

const { Option } = Select;
const { TextArea } = Input;

interface DocumentRequirement {
    id?: number;
    documentTypeCode: string;
    documentTypeName?: string;
    isRequired: boolean;
    isDefault: boolean;
    isVisible: boolean;
    displayOrder: number;
    customLabel?: string;
    validationRules?: string;
    helpText?: string;
}

interface CustomerCategoryDocumentRequirementsProps {
    categoryId?: number;
    documentRequirements: DocumentRequirement[];
    onDocumentRequirementsChange: (requirements: DocumentRequirement[]) => void;
    disabled?: boolean;
}

const CustomerCategoryDocumentRequirements: React.FC<CustomerCategoryDocumentRequirementsProps> = ({
    categoryId,
    documentRequirements,
    onDocumentRequirementsChange,
    disabled = false
}) => {
    const { dark } = useTheme();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRequirement, setEditingRequirement] = useState<DocumentRequirement | null>(null);
    const [form] = Form.useForm();
    
    // Use actual document types from the document type management module
    const { data: documentTypesResponse, isLoading: isLoadingDocumentTypes } = useGetAllDocumentTypes({pageable:{}});

    const handleAddRequirement = () => {
        setEditingRequirement(null);
        form.resetFields();
        form.setFieldsValue({
            isRequired: false,
            isDefault: false,
            isVisible: true,
            displayOrder: documentRequirements.length
        });
        setIsModalVisible(true);
    };

    const handleEditRequirement = (requirement: DocumentRequirement) => {
        setEditingRequirement(requirement);
        form.setFieldsValue(requirement);
        setIsModalVisible(true);
    };

    const handleDeleteRequirement = (documentTypeCode: string) => {
        const updatedRequirements = documentRequirements.filter(
            req => req.documentTypeCode !== documentTypeCode
        );
        onDocumentRequirementsChange(updatedRequirements);
        message.success('Document requirement removed');
    };

    const documentTypes = documentTypesResponse?.content;

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const documentType = documentTypes?.find(dt => dt.code === values.documentTypeCode);
            
            const requirement: DocumentRequirement = {
                ...values,
                documentTypeName: documentType?.name || values.documentTypeCode,
                id: editingRequirement?.id
            };

            let updatedRequirements;
            if (editingRequirement) {
                // Update existing requirement
                updatedRequirements = documentRequirements.map(req =>
                    req.documentTypeCode === editingRequirement.documentTypeCode ? requirement : req
                );
            } else {
                // Add new requirement
                const exists = documentRequirements.some(req => req.documentTypeCode === values.documentTypeCode);
                if (exists) {
                    message.error('Document type already added');
                    return;
                }
                updatedRequirements = [...documentRequirements, requirement];
            }

            onDocumentRequirementsChange(updatedRequirements);
            setIsModalVisible(false);
            message.success(`Document requirement ${editingRequirement ? 'updated' : 'added'} successfully`);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingRequirement(null);
    };

    const handlePropertyChange = (documentTypeCode: string, property: string, value: any) => {
        const updatedRequirements = documentRequirements.map(req => {
            if (req.documentTypeCode === documentTypeCode) {
                return { ...req, [property]: value };
            }
            return req;
        });
        onDocumentRequirementsChange(updatedRequirements);
    };

    const columns = [
        {
            title: 'Document Type',
            key: 'documentType',
            render: (_:any, record: DocumentRequirement) => (
                <div>
                    <div className="font-medium">{record.documentTypeName || record.documentTypeCode}</div>
                    <div className="text-sm text-gray-500">{record.documentTypeCode}</div>
                </div>
            )
        },
        {
            title: 'Custom Label',
            key: 'customLabel',
            render: (_:any, record: DocumentRequirement) => (
                <Input
                    value={record.customLabel}
                    onChange={(e) => handlePropertyChange(record.documentTypeCode, 'customLabel', e.target.value)}
                    placeholder="Optional custom label"
                    disabled={disabled}
                    size="small"
                />
            )
        },
        {
            title: 'Required',
            key: 'isRequired',
            width: 100,
            render: (_:any, record: DocumentRequirement) => (
                <Switch
                    checked={record.isRequired}
                    onChange={(checked) => handlePropertyChange(record.documentTypeCode, 'isRequired', checked)}
                    disabled={disabled}
                />
            )
        },
        {
            title: 'Default',
            key: 'isDefault',
            width: 100,
            render: (_:any, record: DocumentRequirement) => (
                <Tooltip title="Pre-selected when creating customer">
                    <Switch
                        checked={record.isDefault}
                        onChange={(checked) => handlePropertyChange(record.documentTypeCode, 'isDefault', checked)}
                        disabled={disabled}
                    />
                </Tooltip>
            )
        },
        {
            title: 'Visible',
            key: 'isVisible',
            width: 100,
            render: (_:any, record: DocumentRequirement) => (
                <Switch
                    checked={record.isVisible}
                    onChange={(checked) => handlePropertyChange(record.documentTypeCode, 'isVisible', checked)}
                    disabled={disabled}
                />
            )
        },
        {
            title: 'Order',
            key: 'displayOrder',
            width: 100,
            render: (_:any, record: DocumentRequirement) => (
                <InputNumber
                    value={record.displayOrder}
                    onChange={(value) => handlePropertyChange(record.documentTypeCode, 'displayOrder', value || 0)}
                    min={0}
                    disabled={disabled}
                    size="small"
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_:any, record: DocumentRequirement) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEditRequirement(record)}
                            size="small"
                            disabled={disabled}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Remove document requirement?"
                        description="This will remove the document requirement from this category."
                        onConfirm={() => handleDeleteRequirement(record.documentTypeCode)}
                        okText="Yes"
                        cancelText="No"
                        disabled={disabled}
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            disabled={disabled}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const availableDocumentTypes = (documentTypes || []).filter(
        dt => !documentRequirements.some(req => req.documentTypeCode === dt.code)
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className={`text-lg font-semibold ${dark ? 'text-white' : ''}`}>
                        Document Requirements
                    </h3>
                    <p className={`text-sm ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Configure which documents are required, optional, or default for this customer category
                    </p>
                </div>
                {!disabled && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddRequirement}
                        loading={isLoadingDocumentTypes}
                        disabled={isLoadingDocumentTypes}
                    >
                        Add Document Requirement
                    </Button>
                )}
            </div>

            <Table
                dataSource={documentRequirements}
                columns={columns}
                rowKey="documentTypeCode"
                pagination={false}
                size="middle"
                locale={{
                    emptyText: 'No document requirements configured'
                }}
            />

            <Modal
                title={`${editingRequirement ? 'Edit' : 'Add'} Document Requirement`}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={600}
                className={dark ? 'dark' : ''}
            >
                <Form
                    form={form}
                    layout="vertical"
                    className="mt-4"
                >
                    <Form.Item
                        name="documentTypeCode"
                        label="Document Type"
                        rules={[{ required: true, message: 'Please select a document type' }]}
                    >
                        <Select
                            placeholder="Select document type"
                            disabled={!!editingRequirement || isLoadingDocumentTypes}
                            loading={isLoadingDocumentTypes}
                            showSearch
                            optionFilterProp="children"
                        >
                            {(editingRequirement ? (documentTypes || []) : availableDocumentTypes).map(dt => (
                                <Option key={dt.code} value={dt.code}>
                                    <div>
                                        <div className="font-medium">{dt.name}</div>
                                        <div className="text-sm text-gray-500">{dt.description}</div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="customLabel"
                        label="Custom Label"
                        extra="Optional custom label to display instead of the default document type name"
                    >
                        <Input placeholder="e.g., Government ID, Photo ID" />
                    </Form.Item>

                    <div className="grid grid-cols-3 gap-4">
                        <Form.Item
                            name="isRequired"
                            label="Required"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            name="isDefault"
                            label="Default"
                            valuePropName="checked"
                            extra="Pre-selected when creating customer"
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            name="isVisible"
                            label="Visible"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="displayOrder"
                        label="Display Order"
                        rules={[{ required: true, message: 'Please enter display order' }]}
                    >
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder="0"
                        />
                    </Form.Item>

                    <Form.Item
                        name="validationRules"
                        label="Validation Rules"
                        extra="Optional validation rules in JSON format"
                    >
                        <TextArea
                            rows={3}
                            placeholder='{"maxSize": "5MB", "allowedTypes": ["pdf", "jpg", "png"]}'
                        />
                    </Form.Item>

                    <Form.Item
                        name="helpText"
                        label="Help Text"
                        extra="Optional help text to guide users"
                    >
                        <TextArea
                            rows={2}
                            placeholder="Please upload a clear, colored copy of your document"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CustomerCategoryDocumentRequirements;
